import { useCallback } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SpaceContributionForm } from './SpaceContributionForm'
import { useCreateSpaceContribution, useUpdateSpaceContribution } from '@/hooks/useSpaceContributions'
import type { SpaceContributionRow } from '@/types/space-contributions'
import type { SpaceContributionFormValues } from './space-contribution-form-schema'

interface SpaceContributionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  spaceContribution?: SpaceContributionRow | null
}

export function SpaceContributionDrawer({
  open,
  onOpenChange,
  spaceContribution,
}: SpaceContributionDrawerProps) {
  const createMutation = useCreateSpaceContribution()
  const updateMutation = useUpdateSpaceContribution()

  const isLoading = createMutation.isPending || updateMutation.isPending
  const isEdit = !!spaceContribution

  const handleSubmit = useCallback(
    (data: SpaceContributionFormValues) => {
      if (isEdit) {
        updateMutation.mutate(
          { id: spaceContribution!.id, data },
          {
            onSuccess: () => {
              onOpenChange(false)
            },
          }
        )
      } else {
        createMutation.mutate(data, {
          onSuccess: () => {
            onOpenChange(false)
          },
        })
      }
    },
    [isEdit, spaceContribution, createMutation, updateMutation, onOpenChange]
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit ? 'Edit Space Contribution' : 'New Space Contribution'}
          </SheetTitle>
          <SheetDescription>
            {isEdit
              ? 'Update the space contribution details'
              : 'Create a new space contribution entry'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <SpaceContributionForm
            spaceContribution={spaceContribution}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
